import { MatTableDataSource } from '@angular/material/table';
import { FilterModel } from '../controls/filter/filter.model';

export class TylerMatTableDataSource extends MatTableDataSource<any> {

    // Properties
    filterNumber = 0;
    filterTestValue = '';
    // Used when filter value needs to be separate from sorting value
    useFilterDataAccessor = false;
    filters: FilterModel[] = [];
    private _cachedData: any[] = [];

    expandedElements: number[] = [];

    // Class Functions
    constructor(initialData?: any[]) {
        super(initialData);
        this.filterPredicate = this.genericFilter;
    }

    // Sorting Functions
    sortingDataAccessor: (data: any, sortHeaderId: string) => string | number = (data: any, sortHeaderId: string): string | number => {
        // created defaultSortingAccessor so components can override sortingAccessor and still fall back on the default if needed
        return this.defaultSortingAccessor(data, sortHeaderId);
    }

    defaultSortingAccessor: (data: any, sortHeaderId: string) => string | number = (data: any, sortHeaderId: string): string | number => {
        // PRE: a sort or filter is called on a table
        // POST: returns the data for a cell for sorting
        // NOTE: this is overriding the default behavior which type coerces into a number
        // NOTE: this does not work for negative numbers
        const value: any = data[sortHeaderId];
        if (value != undefined) {
            if (value instanceof Date) {
                return value.getTime();
            } else if (typeof (value) == 'string') {
                return (value != null ? value.trim().toLowerCase() : '');
            } else if (typeof (value) == 'number') {
                return (value != null ? value : -99999);
            } else {
                return value;
            }
        } else {
            return '';
        }
    }

    // Filter Functions
    filterDataAccessor: (data: any, sortHeaderId: string) => string | number = (data: any, sortHeaderId: string): string | number => {
        // PRE: a filter is called on a table
        // POST: returns the data for a cell for filtering
        // NOTE: this is overriding the default behavior which type coerces into a number, also overrides the sortingDataAccessor
        // NOTE: this does not work for negative numbers
        const value: any = data[sortHeaderId];
        if (value != undefined) {
            if (value instanceof Date) {
                return value.getTime();
            } else if (typeof (value) == 'string') {
                return value.trim();
            } else {
                return value;
            }
        } else {
            return '';
        }
    }

    // Detail Row Functions
    scrollTo(row: any) {
        // PRE: a row to scroll to
        // POST: scrolls to the row
        // NOTE: this will only work if you are using the scrollToRow directive
        if (row != undefined) {
            if (row.scrollTo == undefined || row.scrollTo == false) {
                row.scrollTo = true;
            }
        }
    }

    scrollToLast() {
        // POST: scrolls to the last row
        this.data[this.data.length - 1].scrollTo = true;
    }

    toggleExpanded(row: any) {
        if (row != undefined) {
            if (row.detailRow == undefined || row.detailRow == false) {
                row.detailRow = true;
            }
            else {
                row.detailRow = false;
            }
        }
    }

    checkExpanded(row: any): boolean {
        if (row.detailRow == undefined) {
            row.detailRow = false;
        }

        return row.detailRow;
    }

    expandAll(expand: boolean) {
        this.data.forEach(element => {
            element.detailRow = expand;
        });
    }

    rowKeyPress(event: KeyboardEvent, row: any) {
        if (event.keyCode == 13) {
            this.toggleExpanded(row);
        }
    }

    cacheExpanded() {
        // PRE: the data is set
        // POST: sets the cached data to the current data
        this._cachedData = this.data;
    }

    assertCachedExpanded(identifier?: string) {
        // PRE: the cached data and data is set
        // POST: expands data based on the cached data's identifier
		if (this._cachedData && this.data) {
			if (!identifier) {
				for (let i = 0; i < this.data.length; i++) {
					if (this._cachedData.length > i && this._cachedData[i].detailRow) {
						this.toggleExpanded(this.data[i]);
					}
				}
			} else {
				this.data.forEach(datum => {
					for (let i = 0; i < this._cachedData.length; i++) {
						const cachedDatum = this._cachedData[i];
						if (cachedDatum[identifier] == datum[identifier] && this.checkExpanded(cachedDatum)) {
							datum.detailRow = true;
							break;
						}
					}
				});
			}
		}
    }

    setVisible(row: any, state: boolean) {
        // PRE: the row is defined
        // POST: toggles whether the row is visible
        // NOTE: this differs from expanded b/c this is meant to work with *ngIf
        if (row != undefined) {
            row.detailRowVisible = state;
        }
    }

    checkVisible(row: any): boolean {
        // PRE: the row is defined
        // POST: checks whether the row is visible
        // NOTE: this differs from expanded b/c this is meant to work with *ngIf
        if (row.detailRowVisible == undefined) {
            row.detailRowVisible = false;
        }

        return row.detailRowVisible;
    }

    // Filter Functions
    genericFilter(Data: any, Filter: string): boolean {

        let valid = true;
        for (let i = 0; i < this.filters.length; i++) {

            let value: string | Date;
            if (this.filters[i].columnIndex) {
                value = Data[this.filters[i].columnIndex];
            }
            else if (Data[this.filters[i].column] instanceof Date) {
                value = Data[this.filters[i].column];
            } else {
                if (this.useFilterDataAccessor) {
                    value = String(this.filterDataAccessor(Data, this.filters[i].column));
                }
                else {
                    value = String(this.sortingDataAccessor(Data, this.filters[i].column));
                }
            }
            if (value != undefined &&
                (
                    (this.filters[i].value != undefined && this.filters[i].dataType != 'date') ||
                    ((this.filters[i].value != undefined || this.filters[i].dateValue != undefined) && this.filters[i].dataType == 'date')
                )) {
                switch (this.filters[i].dataType) {
                    case 'string':
                        if (!this.stringFilter(String(value), i)) {
                            valid = false;
                        }
                        break;
                    case 'number':
                        if (!this.numberFilter(String(value), i)) {
                            valid = false;
                        }
                        break;
                    case 'date':
                        if (!this.dateFilter(value, i)) {
                            valid = false;
                        }
                        break;
                    case 'enumeration':
                        if (!this.enumerationFilter(String(value), i)) {
                            valid = false;
                        }
                        break;
                    case 'nullable boolean':
                    case 'boolean':
                        if (!this.booleanFilter(String(value), i)) {
                            valid = false;
                        }
                        break;
                }
                if (!valid) {
                    break;
                }
            }
        }

        return valid;
    }

    enumerationFilter(dataValue: string, i: number): boolean {
        let valid = true;
        if (this.filters[i].value) {
            switch (this.filters[i].operator) {
                case '0':   // Equal
                    if (dataValue.trim().toLocaleLowerCase() != this.filters[i].value.trim().toLocaleLowerCase()) {
                        valid = false;
                    }
                    break;
                case '1':    // Not Equal
                    if (dataValue.toLocaleLowerCase() == this.filters[i].value.trim().toLocaleLowerCase()) {
                        valid = false;
                    }
                    break;
            }
        }
        return valid;
    }

    stringFilter(dataValue: string, i: number): boolean {

        let valid = true;

        switch (this.filters[i].operator) {
            case '0':
                if (!dataValue.toString().trim().toLocaleLowerCase().includes(this.filters[i].value.trim().toLocaleLowerCase())) {
                    valid = false;
                }
                break;
            case '1':
                if (!dataValue.toString().trim().toLocaleLowerCase().startsWith(this.filters[i].value.trim().toLocaleLowerCase())) {
                    valid = false;
                }
                break;
            case '2':
                if (!dataValue.toString().trim().toLocaleLowerCase().endsWith(this.filters[i].value.trim().toLocaleLowerCase())) {
                    valid = false;
                }
                break;
            case '3':
                if (this.filters[i].value != '' && !(dataValue.toString().trim().toLocaleLowerCase() == (this.filters[i].value.trim().toLocaleLowerCase()))) {
                    valid = false;
                }
                break;
        }

        return valid;
    }

    numberFilter(dataValue: string, i: number) {

        let valid = true;

        const testVar = Number.parseFloat(this.filters[i].value);
        const numberValue = Number.parseFloat(dataValue);

        if (!isNaN(testVar) && testVar != null && testVar != undefined) {
            switch (this.filters[i].operator) {
                case '0':
                    if (!(numberValue == (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
                case '1':
                    if (!(numberValue != (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
                case '2':
                    if (!(numberValue < (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
                case '3':
                    if (!(numberValue <= (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
                case '4':
                    if (!(numberValue > (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
                case '5':
                    if (!(numberValue >= (Number.parseFloat(this.filters[i].value)))) {
                        valid = false;
                    }
                    break;
            }
        }

        return valid;
    }

    dateFilter(dataValue: string | Date, i: number) {
        // console.log('dateFilter');

        let valid = true;

        //  strip time from dateTime values
        const dataDate: Date = new Date(dataValue);
        dataDate.setHours(0);
        dataDate.setMinutes(0);
        dataDate.setSeconds(0);
        let filterDate: Date = new Date(this.filters[i].dateValue);
        if (!filterDate) {
            filterDate = new Date(this.filters[i].value);
        }
        filterDate.setHours(0);
        filterDate.setMinutes(0);
        filterDate.setSeconds(0);

        if (filterDate.toString() != 'Invalid Date') {
            switch (this.filters[i].operator) {
                case '0':
                    if (!(dataDate.toLocaleDateString() == filterDate.toLocaleDateString())) {
                        valid = false;
                    }
                    break;
                case '1':
                    if (!(dataDate.toLocaleDateString() != filterDate.toLocaleDateString())) {
                        valid = false;
                    }
                    break;
                case '2':
                    if (!(dataDate < filterDate)) {
                        valid = false;
                    }
                    break;
                case '3':
                    if (!(dataDate <= filterDate)) {
                        valid = false;
                    }
                    break;
                case '4':
                    if (!(dataDate > filterDate)) {
                        valid = false;
                    }
                    break;
                case '5':
                    if (!(dataDate >= filterDate)) {
                        valid = false;
                    }
                    break;
            }
        }

        return valid;
    }

    booleanFilter(dataValue: string, i: number) {
        switch (this.filters[i].operator) {
            case '0':
                return true;
            case '1':
            case '2':
            case '3':
            default:
                return this.filters[i].value == dataValue;
        }
    }

    isFiltered(): boolean {
        // POST: returns true if the data is being panelFiltered by the data source
        const isFiltering = (filter: FilterModel): boolean => {
            if (filter.dataType.includes('boolean')) {
                return filter.operator != '0';
            } else {
                return (filter.value != '' && filter.value != undefined && filter.value != null) ||
                    (filter.dateValue != undefined && filter.dateValue != null && filter.dateValue.toString() != 'Invalid Date');
            }
        };
        for (let i = 0; i < this.filters.length; i++) {
            if (isFiltering(this.filters[i])) {
                return true;
            }
        }
        return false;
    }

    addRemoveFilter(newFilter: FilterModel, data?: TylerMatTableDataSource) {
        // POST: adds a filter or removes it if the value is ''

        let filterFound = false;

        if (!data) {
            data = this;
        }
        for (let i = 0; i < data.filters.length; i++) {
            if (data.filters[i].column == newFilter.column) {
                if (newFilter.value == '') {
                    data.filters.splice(i, 1);
                    filterFound = true;
                    break;
                }
                else {
                    data.filters[i].operator = newFilter.operator;
                    data.filters[i].value = newFilter.value;
                    filterFound = true;
                    break;
                }
            }
        }

        if (!filterFound) {
            data.filters.push(newFilter);
        }

        data.filter = 'trigger';
    }

    addFilter(newFilter: FilterModel, data?: TylerMatTableDataSource) {
        // POST: adds a filter, regardless of the value

        let filterFound = false;

        if (!data) {
            data = this;
        }
        for (let i = 0; i < data.filters.length; i++) {
            if (data.filters[i].column == newFilter.column) {
                data.filters[i].operator = newFilter.operator;
                data.filters[i].value = newFilter.value;
                filterFound = true;
                break;
            }
        }

        if (!filterFound) {
            data.filters.push(newFilter);
        }

        data.filter = 'trigger';
    }

    removeFilter(filter: FilterModel, data?: TylerMatTableDataSource) {
        // POST: removes the filter, regardless of the data
        if (!data) {
            data = this;
        }
        for (let i = 0; i < data.filters.length; i++) {
            if (data.filters[i].column == filter.column) {
                data.filters.splice(i, 1);
                break;
            }
        }
        data.filter = 'trigger';
    }

    removeAllFilters(data?: TylerMatTableDataSource) {
        // POST: removes all the filters
        if (!data) {
            data = this;
        }
        this.filters = [];
        data.filter = 'trigger';
    }

    getFilter(column: string, data?: TylerMatTableDataSource) {
        // POST: gets a filter for the column
        if (!data) {
            data = this;
        }
        for (let i = 0; i < data.filters.length; i++) {
            if (data.filters[i].column == column) {
                return data.filters[i];
            }
        }
        return null;
    }

    hasFilters() {
        return this.filters.map(o => o.value).filter(value => !!value).length > 0;
    }
}

